import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SegmentedControl, SegmentedControlItem } from '@/components/ui/segmented-control'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { AriaAttributes, ComponentType } from 'react'

export type ViewModeOption = {
  value: string
  label: string
  icon: ComponentType<{ className?: string; 'aria-hidden'?: AriaAttributes['aria-hidden'] }>
  /** Defaults to "<label> view". */
  tooltip?: string
}

export type ViewModeSelectorProps = {
  value: string
  onValueChange: (value: string) => void
  options: ViewModeOption[]
  label?: string
  className?: string
}

export function ViewModeSelector({
  value,
  onValueChange,
  options,
  label = 'View mode',
  className,
}: ViewModeSelectorProps) {
  return (
    <div data-slot="view-mode-selector" className={className}>
      <div className="flex w-full items-center md:hidden">
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger shape="pill" className="w-fit" aria-label={label}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <option.icon aria-hidden="true" className="size-4 shrink-0" />
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <SegmentedControl className="hidden md:inline-flex" role="group" aria-label={label}>
        {options.map((option) => {
          const tooltip = option.tooltip ?? `${option.label} view`
          const isSelected = value === option.value

          return (
            <Tooltip key={option.value}>
              <TooltipTrigger asChild>
                <SegmentedControlItem
                  type="button"
                  aria-label={tooltip}
                  size="sm"
                  selected={isSelected}
                  className="w-11 px-0 has-[>svg]:px-0"
                  onClick={() => onValueChange(option.value)}
                >
                  <option.icon aria-hidden="true" className="size-4 shrink-0" />
                  <span className="sr-only">{option.label}</span>
                </SegmentedControlItem>
              </TooltipTrigger>
              <TooltipContent sideOffset={4}>{tooltip}</TooltipContent>
            </Tooltip>
          )
        })}
      </SegmentedControl>
    </div>
  )
}
